import React, { useState, useMemo, useRef, useEffect } from "react";
import { useUI } from "../../stores/useUI";
import { useLevelProgress } from "../../stores/useLevelProgress";

const ALL_TYPES = "All types";

const C = {
  cream: '#FBF2D3',
  dark: '#521616',
  gradient: 'linear-gradient(180deg, #FF7E88 0%, #FF9A88 100%)',
  filterBar: '#EF767E',
  chipOff: '#EAA299',
  titleColor: '#843B37',
  mono: '"IBM Plex Mono", monospace',
  title: '"Sixtyfour", monospace',
};

const StatsPopup = () => {
  const { statsPopupIsOpen, setStatsPopupIsOpen, setCurrentMap, currentMap } = useUI();
  const { getMapData, getMaps, isMapUnlocked, isNPCCompleted, isNPCUnlocked } = useLevelProgress();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(ALL_TYPES);
  const [previousCategory, setPreviousCategory] = useState(ALL_TYPES);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [hoveredMapId, setHoveredMapId] = useState<string | null>(null);

  const chipRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [highlightStyle, setHighlightStyle] = useState({ left: 0, width: 0, top: 0, height: 0, opacity: 0 });
  const [headerBottom, setHeaderBottom] = useState(120);

  useEffect(() => {
    if (statsPopupIsOpen) {
      setSearchQuery("");
      setSearchOpen(false);
    }
  }, [statsPopupIsOpen]);

  useEffect(() => {
    if (!currentMap) return;
    const header = document.querySelector('[data-main-header]');
    if (!header) return;
    const update = () => setHeaderBottom(header.getBoundingClientRect().bottom + 16);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(header);
    return () => observer.disconnect();
  }, [currentMap]);

  const currentMapNPCs = useMemo(() => {
    if (!currentMap) return [];
    const mapData = getMapData()[currentMap];
    if (!mapData) return [];
    return mapData.npcs.map(npc => ({
      ...npc,
      isUnlocked: isNPCUnlocked(currentMap, npc.id),
      isCompleted: isNPCCompleted(currentMap, npc.id),
    }));
  }, [currentMap, getMapData, isNPCUnlocked, isNPCCompleted, statsPopupIsOpen]);

  const processedMaps = useMemo(() => {
    const mapConfigs = getMaps();
    const details = getMapData();
    return mapConfigs.map(config => ({
      ...config,
      category: details[config.id]?.category || "Uncategorized",
      isUnlocked: isMapUnlocked(config.id),
    }));
  }, [getMaps, getMapData, isMapUnlocked, statsPopupIsOpen]);

  const categories = useMemo(() => {
    const cats = new Set(processedMaps.map(m => m.category).filter(c => c !== "Uncategorized"));
    return [ALL_TYPES, ...Array.from(cats).sort()];
  }, [processedMaps]);

  useEffect(() => {
    if (searchOpen) return;
    const index = categories.findIndex(cat => cat === selectedCategory);
    const el = chipRefs.current[index];
    if (el) {
      setHighlightStyle({
        left: el.offsetLeft,
        width: el.offsetWidth,
        top: el.offsetTop,
        height: el.offsetHeight,
        opacity: 1,
      });
    }
  }, [selectedCategory, categories, statsPopupIsOpen, searchOpen]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (val.length > 0 && searchQuery.length === 0) {
      setPreviousCategory(selectedCategory);
      setSelectedCategory(ALL_TYPES);
    } else if (val.length === 0) {
      setSelectedCategory(previousCategory);
    }
  };

  const handleCategoryClick = (cat: string) => {
    setSelectedCategory(cat);
    setSearchQuery("");
    setSearchOpen(false);
  };

  const toggleSearch = () => {
    if (searchOpen) {
      setSearchOpen(false);
      setSearchQuery("");
      setSelectedCategory(previousCategory);
    } else {
      setPreviousCategory(selectedCategory);
      setSearchOpen(true);
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  };

  const filteredMaps = useMemo(() => {
    return processedMaps.filter(map => {
      if (searchQuery.length > 0) {
        return map.isUnlocked && map.name.toLowerCase().includes(searchQuery.toLowerCase());
      }
      return selectedCategory === ALL_TYPES || map.category === selectedCategory;
    });
  }, [processedMaps, searchQuery, selectedCategory]);

  const handleMapPlay = (mapId: string) => {
    setCurrentMap(mapId);
    setStatsPopupIsOpen(false);
  };

  const catLabel = (cat: string) => cat.charAt(0).toUpperCase() + cat.slice(1);

  const renderFilterBar = () => (
    <div style={s.filterBar}>
      <div style={s.chipsRow}>
        {categories.map((cat, i) => {
          const isActive = cat === selectedCategory && !searchOpen;
          return (
            <button
              key={cat}
              ref={el => { chipRefs.current[i] = el; }}
              onClick={() => handleCategoryClick(cat)}
              style={{
                ...s.chip,
                background: isActive ? C.cream : C.chipOff,
                boxShadow: isActive
                  ? 'inset -3px -3px 0px rgba(212, 28, 28, 0.15)'
                  : 'inset -3px -3px 0px rgba(212, 28, 28, 0.25)',
              }}
            >
              {catLabel(cat)}
            </button>
          );
        })}
      </div>
      {searchOpen ? (
        <div style={s.searchPill}>
          <style>{`
            .stats-search::placeholder { color: rgba(82, 22, 22, 0.45); }
          `}</style>
          <img src='/icons/magnifying_glass.svg' alt="Search" width={20} height={20} style={{ flexShrink: 0, filter: 'invert(13%) sepia(50%) saturate(800%) hue-rotate(320deg) brightness(70%)' }} />
          <input
            ref={searchInputRef}
            className="stats-search"
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearchChange}
            style={s.searchInput}
          />
          <button onClick={toggleSearch} style={s.searchClose}>✕</button>
        </div>
      ) : (
        <button onClick={toggleSearch} style={s.searchBtn}>
          <img src='/icons/magnifying_glass.svg' alt="Search" width={20} height={20} />
        </button>
      )}
    </div>
  );

  const renderMapList = () => (
    <div style={s.list}>
      {filteredMaps.map((map) => {
        const hovered = hoveredMapId === map.id;
        return (
          <div
            key={map.id}
            onClick={() => map.isUnlocked && handleMapPlay(map.id)}
            style={{
              ...s.row,
              opacity: map.isUnlocked ? 1 : 0.5,
              background: hovered && map.isUnlocked ? C.cream : '#F3C6B3',
              transition: 'background 0.15s',
              cursor: map.isUnlocked ? 'pointer' : 'not-allowed',
            }}
            onMouseEnter={() => setHoveredMapId(map.id)}
            onMouseLeave={() => setHoveredMapId(null)}
          >
            <img
              src={`/islands/icons/${map.category}.png`}
              alt={map.name}
              style={s.rowIcon}
            />
            <span style={s.rowName}>
              {map.isUnlocked ? map.name : '???'}
            </span>
            <span style={s.rowStatus}>
              {map.isUnlocked ? 'Discovered' : 'Locked'}
            </span>
            <span style={{
              ...s.arrowBtn,
              color: map.isUnlocked ? C.dark : 'transparent',
            }}>
              →
            </span>
          </div>
        );
      })}
      {filteredMaps.length === 0 && (
        <div style={s.emptyState}>No maps found</div>
      )}
    </div>
  );

  const renderNPCList = () => (
    <div style={s.list}>
      {currentMapNPCs.map((npc, idx) => (
        <div
          key={npc.id}
          style={{
            ...s.row,
            background: idx % 2 === 0 ? C.cream : '#F3C6B3',
            opacity: npc.isCompleted ? 1 : npc.isUnlocked ? 0.75 : 0.5,
          }}
        >
          <span style={s.rowName}>{npc.name}</span>
          <span style={s.rowStatus}>
            {npc.isCompleted ? 'Completed' : npc.isUnlocked ? 'Available' : 'Locked'}
          </span>
        </div>
      ))}
      {currentMapNPCs.length === 0 && (
        <div style={s.emptyState}>No NPCs found</div>
      )}
    </div>
  );

  return (
    <div style={{
      ...s.overlay,
      opacity: statsPopupIsOpen ? 1 : 0,
      pointerEvents: statsPopupIsOpen ? 'auto' : 'none',
    }}>
      <div style={{
        ...s.popup,
        paddingTop: currentMap ? headerBottom : 120,
        transform: statsPopupIsOpen ? 'scale(1)' : 'scale(0.96)',
        opacity: statsPopupIsOpen ? 1 : 0,
      }}>
        {currentMap ? renderNPCList() : (
          <>
            {renderFilterBar()}
            {renderMapList()}
          </>
        )}
      </div>
    </div>
  );
};

//########################## STYLES ###########################

const s: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    transition: 'opacity 0.25s ease',
  },
  popup: {
    position: 'relative',
    width: '100%',
    height: '100%',
    background: C.gradient,
    padding: '120px 32px 32px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    overflow: 'hidden',
    transition: 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease',
  },
  filterBar: {
    background: C.filterBar,
    border: `1px solid ${C.dark}`,
    borderRadius: '70px',
    display: 'flex',
    alignItems: 'center',
    padding: '8px',
    gap: '8px',
    flexShrink: 0,
    height: '66px',
  },
  chipsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderRadius: '70px',
    flex: 1,
    position: 'relative',
    overflowX: 'auto',
    overflowY: 'hidden',
    height: '50px',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },
  chipHighlight: {
    position: 'absolute',
    borderRadius: '93px',
    background: C.cream,
    border: `1px solid ${C.dark}`,
    boxShadow: 'inset -3px -3px 0px rgba(212, 28, 28, 0.25)',
    zIndex: 0,
    pointerEvents: 'none',
    transition: 'all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)',
  },
  chip: {
    background: C.chipOff,
    border: `1px solid ${C.dark}`,
    boxShadow: 'inset -3px -3px 0px rgba(212, 28, 28, 0.25)',
    borderRadius: '93px',
    color: C.dark,
    fontFamily: C.mono,
    fontSize: '20px',
    fontWeight: 400,
    padding: '6px 34px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    position: 'relative',
    zIndex: 1,
    lineHeight: '26px',
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    justifyContent: 'center',
  },
  searchPill: {
    background: C.cream,
    border: `1px solid ${C.dark}`,
    boxShadow: 'inset -3px -3px 0px rgba(212, 28, 28, 0.25)',
    borderRadius: '93px',
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    padding: '0 12px 0 16px',
    gap: '8px',
    flexShrink: 0,
    minWidth: '200px',
  },
  searchInput: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    fontFamily: C.mono,
    fontSize: '20px',
    color: C.dark,
    minWidth: 0,
  },
  searchClose: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: C.dark,
    fontSize: '16px',
    padding: '0 4px',
    flexShrink: 0,
    lineHeight: 1,
  },
  searchBtn: {
    background: C.chipOff,
    border: `1px solid ${C.dark}`,
    boxShadow: 'inset -3px -3px 0px rgba(212, 28, 28, 0.25)',
    borderRadius: '93px',
    width: '66px',
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
  list: {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  row: {
    background: C.cream,
    border: `1px solid ${C.dark}`,
    boxShadow: 'inset -3px -3px 0px rgba(212, 28, 28, 0.25)',
    borderRadius: '93px',
    padding: '6px 21px',
    display: 'flex',
    alignItems: 'center',
    gap: '21px',
    height: '70px',
    flexShrink: 0,
  },
  rowIcon: {
    width: '51px',
    height: '49px',
    objectFit: 'contain',
    flexShrink: 0,
  },
  rowName: {
    fontFamily: C.mono,
    fontSize: '20px',
    color: C.dark,
    fontWeight: 400,
    lineHeight: '26px',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowStatus: {
    fontFamily: C.mono,
    fontSize: '20px',
    color: C.dark,
    fontWeight: 400,
    lineHeight: '26px',
    flexShrink: 0,
  },
  arrowBtn: {
    background: 'transparent',
    border: `1px solid transparent`,
    width: '42px',
    height: '42px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: C.mono,
    fontSize: '20px',
    color: C.dark,
    flexShrink: 0,
  },
  emptyState: {
    fontFamily: C.mono,
    fontSize: '16px',
    color: C.dark,
    textAlign: 'center',
    marginTop: '40px',
    opacity: 0.6,
  },
};

export default StatsPopup;
